import {useState} from 'react';
import {useSession} from '../../../../firebase/UserProvider';
import {
    createReport,
    deleteDisclosure,
    deleteReport,
    resetReportToDraft,
} from '../../../../firebase/firestore/reports';
import {triggerExpertProfileUpdateForAuthors} from '../../../experts/expertProfileService';
import {
    DEFAULT_DISCLOSURE_HEADER_DATA,
    DISCLOSURE_TYPE_OPTIONS,
    PURPOSE_OPTIONS,
} from '../../../../components/prosemirror-editor/components/documentHeader/disclosureFieldOptions';

import {exportReport} from '../../../../utils/exportReportHelper';

/**
 * Custom hook for table row actions in the unified products list.
 *
 * Reproduces the original per-category menus (Drafts / FINTEL / Disclosures) on a single row by
 * deriving the report's context: draft vs approved, and disclosure vs analytic (FINTEL).
 *
 * @param {string} reportId - The report ID
 * @param {string} title - The report title
 * @param {object} report - Full report object
 * @param {boolean} enableDisclosureCreation - Force-enable disclosure creation regardless of context
 * @returns {object} Action handlers, loading states, role, and context flags
 */
export function useTableRowActions({reportId, title, report, enableDisclosureCreation = false}) {
    const {user, roles} = useSession();
    const isAdmin = Array.isArray(roles) && roles.includes('admin');
    const [deleting, setDeleting] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [resettingToDraft, setResettingToDraft] = useState(false);

    const ownerUid = report?.userId || report?.owner;
    const role = user?.uid && ownerUid && user.uid === ownerUid ? 'owner' : null;
    const isShared = Boolean(user?.uid && ownerUid && user.uid !== ownerUid);
    const canModerate = role === 'owner' || isAdmin;

    // Report context — drives which menu actions apply (mirrors the old category tables).
    const isApproved = (report?.status || '').toLowerCase() === 'approved';
    const productTypeValue = report?.productType?.value;
    const isDisclosure = productTypeValue === 'disclosure' || productTypeValue === 'ndp1_disclosure';
    // FINTEL = an approved, non-disclosure product (the only rows that can spawn a disclosure).
    const isFintel = isApproved && !isDisclosure;

    const handleDelete = async () => {
        if (!reportId) return;
        try {
            setDeleting(true);

            const isDisclosure = report?.productType?.value === 'disclosure' ||
                report?.productType?.value === 'ndp1_disclosure';

            if (isDisclosure) {
                await deleteDisclosure(reportId, report);
            } else {
                await deleteReport(reportId);
            }
        } catch (e) {
            console.error('Failed to delete report', e);
            throw e;
        } finally {
            setDeleting(false);
        }
    };

    const handleExport = async () => {
        setExporting(true);
        try {
            await exportReport({reportId, title, report, fallbackPrefix: 'report'});
        } catch (err) {
            console.error('Error exporting to MS Word:', err);
            throw err;
        } finally {
            setExporting(false);
        }
    };

    const handleCreateDisclosure = async () => {
        if (!reportId || !user) return null;

        // Defaults mirror the original FINTEL flow: NDP-1 disclosure with the first purpose option.
        const selectedDisclosureType =
            DISCLOSURE_TYPE_OPTIONS.find(opt => opt.value === 'NDP-1')
            || DISCLOSURE_TYPE_OPTIONS[0]
            || DEFAULT_DISCLOSURE_HEADER_DATA.disclosureType;
        const selectedPurpose = PURPOSE_OPTIONS[0] || DEFAULT_DISCLOSURE_HEADER_DATA.purpose;

        try {
            const disclosurePayload = {
                title: `Disclosure - ${title}`,
                topic: report.topic || '',
                defaultQuery: report.defaultQuery || '',
                productType: {value: 'disclosure', label: 'Disclosure'},
                userId: user.uid,
                userDisplayName: user.displayName || 'Anonymous',
                status: 'Draft',
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString(),
                parent_product: {
                    uid: reportId,
                    title: title,
                    productType: report.productType,
                    meta: report.meta || {},
                    snapshotPath: report.snapshotPath || ''
                },
                meta: {
                    title: `Disclosure - ${title}`,
                    icrNumber: report.meta?.icrNumber || null,
                    productType: {value: 'disclosure', label: 'Disclosure'},
                    parentReportId: reportId,
                    parent_product: {
                        uid: reportId,
                        title: title,
                        productType: report.productType,
                        meta: report.meta || {},
                        snapshotPath: report.snapshotPath || ''
                    },
                    dtg: report.meta?.dtg || '',
                    originator: report.meta?.originator || {value: '', label: 'Select originator'},
                    classification: report.meta?.classification || {value: 'U', label: 'U - Unclassified'},
                    distribution: report.meta?.distribution || [],
                    compartment: report.meta?.compartment || [],
                    confidence: report.meta?.confidence || {value: '', label: 'Select confidence'},
                    ifc: report.meta?.ifc || {value: '', label: 'Select IFC'},
                    productionCenter: report.meta?.productionCenter || {value: '', label: 'Select production center'},
                    disclosureType: selectedDisclosureType,
                    purpose: selectedPurpose,
                }
            };

            const {reportId: disclosureId} = await createReport(disclosurePayload);
            return disclosureId;
        } catch (err) {
            console.error('Error creating disclosure draft:', err);
            throw err;
        }
    };

    const handleResetToDraft = async () => {
        if (!reportId) return;
        try {
            setResettingToDraft(true);
            // Capture authors BEFORE resetting so their expert profiles can still be refreshed.
            const authors = Array.isArray(report?.meta?.authors) ? report.meta.authors : [];
            await resetReportToDraft(reportId);
            if (authors.length > 0) {
                triggerExpertProfileUpdateForAuthors(authors).catch((err) =>
                    console.error('[useTableRowActions] Expert profile update failed:', err)
                );
            }
        } catch (e) {
            console.error('Failed to reset report to Draft', e);
            throw e;
        } finally {
            setResettingToDraft(false);
        }
    };

    // Create-disclosure is offered for FINTEL rows (approved analytic products), or when the
    // caller explicitly forces it on. Reset-to-draft is owner-only.
    const canCreateDisclosure = enableDisclosureCreation || isFintel;
    const canResetToDraft = isFintel && role === 'owner';
    // Delete: drafts are owner-only (as before); approved/disclosure rows allow moderators.
    const canDelete = (isApproved || isDisclosure) ? canModerate : role === 'owner';

    return {
        handleDelete,
        handleExport,
        handleCreateDisclosure: canCreateDisclosure ? handleCreateDisclosure : null,
        handleResetToDraft: canResetToDraft ? handleResetToDraft : null,
        deleting,
        exporting,
        resettingToDraft,
        role,
        isShared,
        isAdmin,
        canModerate,
        canDelete,
        isApproved,
        isDisclosure,
        isFintel,
    };
}
